import {IEventManagerWithAsync} from '@fantasmagames/core/src/events/_interfaces/IEventManager';
import {CurrencyFormatter} from '@fantasmagames/core/src/utils/CurrencyFormatter';
import {QuerystringHandler} from '@fantasmagames/core/src/utils/QuerystringHandler';
import {Action} from '@fantasmagames/slot/src/logic/Action';
import {Communicator} from '@fantasmagames/slot/src/logic/Communicator';
import {SpinResult} from '@fantasmagames/slot/src/logic/SpinResult';
import {BaseStorage, UserStorage} from '@fantasmagames/slot/src/logic/Storage';
import {IAutoplaySettings} from '@fantasmagames/slot/src/modules/autoplay/AutoplayHandlerModule';
import {SymbolPosition} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {GameUtils} from '@fantasmagames/slot/src/utils/GameUtils';
import {injectable} from 'inversify';
import {Device} from '@fantasmagames/core/src/device/Device';
import {GameConfig} from '@fantasmagames/slot/src/config/GameConfig';

export interface RespinSymbolData {
	symbol: number;
	position: SymbolPosition;
}

@injectable()
export class Logic {
	public static CENT_TO_A_DOLLAR: number = 0.01;

	protected _betLevel: number = 0;
	protected _betLevels: number[];
	protected _betValue: number = 0;
	protected _betValues: number[];
	protected _defaultBet: number;
	protected _rawBetValues: number[];
	protected _rawBetValue: number = 0;

	protected _skipIntro: boolean = false;
	protected _spacebarSpinActive: boolean = true;
	protected _dragReelToSpinActive: boolean = true;
	protected _quickStopActive: boolean = true;
	protected _fastPlayActive: boolean = false;

	protected _voiceActive: boolean = true;
	protected _soundEffectsActive: boolean = true;
	protected _musicActive: boolean = true;
	protected _soundAllActive: boolean = true;

	protected _quickSpinActive: boolean;
	protected _lastBet: number;
	protected _lastBetWasMaxBet: boolean;
	protected _lastAutoplayAmount: number;

	protected _tokenID: string;
	protected _regulatedMarket: string;

	protected _autoplayValues: number[] = [5, 10, 25, 50, 100];
	protected _autoplayDefault: number = 25;
	protected _autoplayData: IAutoplaySettings;

	protected _balance: number = 100;
	protected _preSpinBalance: number;
	protected _freeBalance: number;
	protected _totalBet: number = 0;
	protected _sessionID: string;
	protected _gameName: string;
	protected _previousAction: Action = Action.NONE;
	protected _previousSpinRoundWin: number;
	protected _eventManager: IEventManagerWithAsync;
	protected _nextAction: Action = Action.SPIN;
	protected _spin: SpinResult;

	protected _responsibleGaming: boolean = false;
	protected _paytable: IPaytable;

	protected _hasRestore: boolean = false;
	protected _hasSavedData: boolean;

	protected _communicator: Communicator;
	protected _currencyFormatter: CurrencyFormatter;
	protected _queryStringHandler: QuerystringHandler;
	protected _storage: UserStorage;

	protected _isFreeSpinsStarted: boolean = false;

	protected _freespinsTotalAwarded: number = 0;
	protected _freeSpinsLeft: number = 0;
	protected _freeSpinsTotalWin: number = 0;
	protected _freeSpinsMultiplier: number = 1;
	protected _freeSpinsPreviousMultiplier: number = 1;
	protected _hasFreeSpinsRetrigger: boolean;
	protected _freeSpinsAdded: number;

	protected _config: GameConfig;

	protected _device: Device;

	constructor(gameName: string, eventManager: IEventManagerWithAsync, communicator: Communicator, device: Device) {
		this._eventManager = eventManager;
		this._gameName = gameName;
		this._device = device;

		this.createStorage();
		this.createCurrencyFormatter();
		this.createQuerystringHandler();
		this.createCommunicator(communicator);
	}

	public initializeConfig(configData: any): void {
		if (!this._config) {
			this._config = new GameConfig();
		}
		this._config.loadConfig(configData);
	}

	public isEnoughMoneyForSpin(): boolean {
		return this._balance >= this.getTotalBet();
	}

	public ping(cb: Function): void {
		throw new Error('Logic::init must be implemented');
	}

	public init(cb: Function): void {
		throw new Error('Logic::init must be implemented');
	}

	public spin(cb: Function): void {
		throw new Error('Logic::spin must be implemented');
	}

	public freeSpin(cb: Function): void {
		throw new Error('Logic::freeSpin must be implemented');
	}

	public respin(cb: Function): void {
		throw new Error('Logic::respin must be implemented');
	}

	public freespin(cb: Function): void {
		throw new Error('Logic::freespin must be implemented');
	}

	public avalanche(cb: Function): void {
		throw new Error('Logic::avalanche must be implemented');
	}

	public pick(cb: Function): void {
		throw new Error('Logic::pick must be implemented');
	}

	public skippick(cb: Function): void {
		throw new Error('Logic::skippick must be implemented');
	}

	public initAutoplayDefaultValues(): void {
		this.autoplayDefault = 25;
		this.autoplayValues = [10, 25, 50, 100, 200, 500];
	}

	public getSavedData(): any {
		return JSON.parse(this._storage.getItem(`FNG_${this._gameName}`));
	}

	protected generateDataToSave(): any {
		const autoplayData = {autoplayValue: this.autoplayDefault};

		// tODO: Verify if lastbet is correct.
		const lastBet = this.betValue;
		const lastBetWasMaxBet = this.isMaxBet(this.betValue);
		const lastAutoplayAmount = this.lastAutoplayAmount;
		const soundEffectsActive = this.soundEffectsActive;
		const voiceActive = this.voiceActive;
		const musicActive = this.musicActive;
		const soundAllActive = this.soundAllActive;
		const skipIntro = this.skipIntro;
		const spacebarSpinActive = this.spacebarSpinActive;
		const dragReelToSpinActive = this.dragReelToSpinActive;

		const data = {
			playerData: {
				autoplayData,
				lastBet,
				lastBetWasMaxBet,
				lastAutoplayAmount,
				soundEffectsActive,
				voiceActive,
				musicActive,
				soundAllActive,
				skipIntro,
				spacebarSpinActive,
				dragReelToSpinActive,
			},
		};

		return data;
	}

	public saveData(): void {
		const data: any = this.generateDataToSave();
		this._storage.setItem(`FNG_${this._gameName}`, JSON.stringify(data));
	}

	public startNextRound(): void {
		throw new Error('Logic::startNextRound must be overridden');
	}

	public killRestore(): void {
		this._hasRestore = false;
	}

	public getSpinResult(): SpinResult {
		return this._spin;
	}

	public getWin(): number {
		if (this.getSpinResult()) {
			return isNaN(this.getSpinResult().win) ? 0 : this.getSpinResult().win;
		}

		return 0;
	}

	public getSpinWin(): number {
		if (this.getSpinResult()) {
			return isNaN(this.getSpinResult().spinWin) ? 0 : this.getSpinResult().spinWin;
		}

		return 0;
	}

	public getNextSpinWin(): number {
		if (this.getSpinResult()) {
			return isNaN(this.getSpinResult().nextSpinWin) ? 0 : this.getSpinResult().nextSpinWin;
		}

		return 0;
	}

	public resetRound(): void {}

	public getBonusWin(): number {
		return 0;
	}

	public getTotalBet(): number {
		return this.betValue;
	}

	public getSpinBalance(): number {
		if (this.getSpinResult()) {
			return this.getSpinResult().balance;
		}

		return 0;
	}

	public finishFreeSpins(): void {
		this._nextAction = Action.SPIN;
	}

	public startFreeSpinsIntro(): void {
		this._nextAction = Action.FREESPIN;

	}

	public isFreeSpinsJustWon(): boolean {
		return this.freeSpinsLeft === this.freespinsTotalAwarded && this.freeSpinsLeft > 0;
	}

	protected createStorage(): void {
		this._storage = new BaseStorage();
	}

	protected createCommunicator(communicator: Communicator): void {
		if (this._queryStringHandler) {
			this.setCommunicatorValues();
		}
	}

	protected createQuerystringHandler(): void {
		this._queryStringHandler = new QuerystringHandler();

		this.setTokenID(this._queryStringHandler);
		this.setRegulatedMarket(this._queryStringHandler);
	}

	protected createCurrencyFormatter(): void {
		this._currencyFormatter = new CurrencyFormatter();
	}

	protected setCommunicatorValues(): void {
		this._communicator.tokenID = this.tokenID;
		this._communicator.regulatedMarket = this.regulatedMarket;
	}

	protected parseSessionID(data: any): void {
		this._sessionID = data.sessionId;
	}

	protected parsePlayerSavedData(): void {
		const data = this.getSavedData();

		if (data && data.playerData) {
			const playerData = data.playerData;

			if (typeof playerData.quickSpinActive !== 'undefined') {
				this.quickSpinActive = playerData.quickSpinActive;
			}

			if (typeof playerData.spacebarSpinActive !== 'undefined') {
				this.spacebarSpinActive = playerData.spacebarSpinActive;
			}

			if (typeof playerData.dragReelToSpinActive !== 'undefined') {
				this.dragReelToSpinActive = playerData.dragReelToSpinActive;
			}

			if (typeof playerData.quickStopActive !== 'undefined') {
				this.quickStopActive = playerData.quickStopActive;
			}

			if (typeof playerData.fastPlayActive !== 'undefined') {
				this.fastPlayActive = playerData.fastPlayActive;
			}

			if (typeof playerData.soundEffectsActive !== 'undefined') {
				this.soundEffectsActive = playerData.soundEffectsActive;
			}

			if (typeof playerData.voiceActive !== 'undefined') {
				this.voiceActive = playerData.voiceActive;
			}

			if (typeof playerData.musicActive !== 'undefined') {
				this.musicActive = playerData.musicActive;
			}

			if (typeof playerData.soundAllActive !== 'undefined') {
				this.soundAllActive = playerData.soundAllActive;
			}

			if (typeof playerData.lastBet !== 'undefined') {
				this.lastBet = playerData.lastBet;
				this.betValue = playerData.lastBet;
			}

			if (typeof playerData.lastBetWasMaxBet !== 'undefined') {
				this._lastBetWasMaxBet = playerData.lastBetWasMaxBet;
			}

			if (typeof playerData.skipIntro !== 'undefined') {
				this.skipIntro = playerData.skipIntro;
			}

			if (typeof playerData.autoplayData !== 'undefined') {
				this.autoplayDefault = playerData.autoplayData.autoplayValue;
			}

			if (typeof playerData.lastAutoplayAmount !== 'undefined') {
				this.lastAutoplayAmount = playerData.lastAutoplayAmount;
			}

			this.hasSavedData = true;
		}
	}

	protected isMaxBet(betvalue: number): boolean {
		return betvalue === this.betValues[this.betValues.length - 1];
	}

	protected getLowerBet(betvalue: number): number {
		let value = this.betValues[0];

		for (const element of this.betValues) {
			if (element < betvalue) {
				value = element;
			}
		}

		return value;
	}

	protected getHigherBet(betvalue: number): number {
		let value = this.betValues[this.betValues.length - 1];

		for (const element of this.betValues) {
			if (element >= betvalue) {
				value = element;
			}
		}

		return value;
	}

	protected getClosestBet(betvalue: number): number {
		let value = this.betValues[this.betValues.length - 1];

		for (let index = this.betValues.length - 1; index >= 0; index--) {
			const element = this.betValues[index];

			if (betvalue <= element) {
				value = element;
			}
		}

		return value;
	}

	protected createSpinResult(): SpinResult {
		return new SpinResult();
	}

	private setCurrencySign(queryStringHandler: QuerystringHandler): void {
		if (queryStringHandler.getValue('currencyPrefix') !== '') {
			this.currencyFormatter.currencyPrefix = queryStringHandler.getValue('currencyPrefix');
		}
		if (queryStringHandler.getValue('currencySuffix') !== '') {
			this.currencyFormatter.currencySuffix = queryStringHandler.getValue('currencySuffix');
		}
	}

	private setTokenID(queryStringHandler: QuerystringHandler): void {
		if (queryStringHandler.getValue('userId') !== '') {
			this.tokenID = queryStringHandler.getValue('userId');
		} else {
			this.tokenID = GameUtils.randomInt(100001, 999999).toString();
		}
	}

	private setRegulatedMarket(queryStringHandler: QuerystringHandler): void {
		if (queryStringHandler.getValue('rm') !== '') {
			this._regulatedMarket = queryStringHandler.getValue('rm');
		} else {
			this._regulatedMarket = '';
		}
	}

	public get previousAction(): Action {
		return this._previousAction;
	}

	public get nextAction(): Action {
		return this._nextAction;
	}

	public get betValues(): number[] {
		return this._betValues;
	}

	public get defaultBetValue(): number {
		return this._defaultBet;
	}

	public get betValue(): number {
		return this._betValue;
	}

	public set betValue(betvalue: number) {
		this._betValue = betvalue;
	}

	public get autoplayDefault(): number {
		return this._autoplayDefault;
	}

	public set autoplayDefault(value: number) {
		this._autoplayDefault = value;
	}

	public set autoplayValues(values: number[]) {
		this._autoplayValues = values;
	}

	public get autoplayValues(): number[] {
		return this._autoplayValues;
	}

	public get hasRestore(): boolean {
		return this._hasRestore;
	}

	public set soundEffectsActive(value: boolean) {
		this._soundEffectsActive = value;
	}

	public get soundEffectsActive(): boolean {
		return this._soundEffectsActive;
	}

	public set voiceActive(value: boolean) {
		this._voiceActive = value;
	}

	public get voiceActive(): boolean {
		return this._voiceActive;
	}

	public set spacebarSpinActive(value: boolean) {
		this._spacebarSpinActive = value;
	}

	public get spacebarSpinActive(): boolean {
		return this._spacebarSpinActive;
	}

	public set dragReelToSpinActive(value: boolean) {
		this._dragReelToSpinActive = value;
	}

	public get dragReelToSpinActive(): boolean {
		return this._dragReelToSpinActive;
	}

	public set fastPlayActive(value: boolean) {
		this._fastPlayActive = value;
	}

	public get fastPlayActive(): boolean {
		return this._fastPlayActive;
	}

	public set quickStopActive(value: boolean) {
		this._quickStopActive = value;
	}

	public get quickStopActive(): boolean {
		return this._quickStopActive;
	}

	public set musicActive(value: boolean) {
		this._musicActive = value;
	}

	public get musicActive(): boolean {
		return this._musicActive;
	}

	public set skipIntro(value: boolean) {
		this._skipIntro = value;
	}

	public get skipIntro(): boolean {
		return this._skipIntro;
	}

	public set soundAllActive(value: boolean) {
		this._soundAllActive = value;
	}

	public get soundAllActive(): boolean {
		return this._soundAllActive;
	}

	public get currentBalance(): number {
		return this._balance;
	}

	public set preSpinBalance(value: number) {
		this._preSpinBalance = value;
	}

	public get preSpinBalance(): number {
		return this._preSpinBalance;
	}

	public set lastBet(value: number) {
		this._lastBet = value;
	}

	public get lastBet(): number {
		return this._lastBet;
	}

	public set lastAutoplayAmount(value: number) {
		this._lastAutoplayAmount = value;
	}

	public get lastAutoplayAmount(): number {
		return this._lastAutoplayAmount;
	}

	public set quickSpinActive(value: boolean) {
		this._quickSpinActive = value;
	}

	public get quickSpinActive(): boolean {
		return this._quickSpinActive;
	}

	public set hasSavedData(value: boolean) {
		this._hasSavedData = value;
	}

	public get hasSavedData(): boolean {
		return this._hasSavedData;
	}

	public get currencyFormatter(): CurrencyFormatter {
		return this._currencyFormatter;
	}

	public get regulatedMarket(): string {
		return this._regulatedMarket;
	}

	public get paytable(): IPaytable {
		return this._paytable;
	}

	public set responsibleGaming(value: boolean) {
		this._responsibleGaming = value;
	}

	public get responsibleGaming(): boolean {
		return this._responsibleGaming;
	}

	private set tokenID(value: string) {
		this._tokenID = value;
	}

	private get tokenID(): string {
		return this._tokenID;
	}

	public get communicator(): Communicator {
		return this._communicator;
	}

	public get autoplayData(): IAutoplaySettings {
		return this._autoplayData;
	}

	public get isFreeSpinsStarted(): boolean {
		return this._isFreeSpinsStarted;
	}

	public set isFreeSpinsStarted(value: boolean) {
		this._isFreeSpinsStarted = value;
	}

	public get freeSpinsLeft(): number {
		return this._freeSpinsLeft;
	}

	public get freeSpinsPlayed(): number {
		return this._freespinsTotalAwarded - this._freeSpinsLeft;
	}

	public get freespinsTotalAwarded(): number {
		return this._freespinsTotalAwarded;
	}

	public get freeSpinsMultiplier(): number {
		return this._freeSpinsMultiplier;
	}

	public get freeSpinsTotalWin(): number {
		return this._freeSpinsTotalWin;
	}

	public get freeSpinsPreviousMultiplier(): number {
		return this._freeSpinsPreviousMultiplier;
	}

	public get config(): GameConfig {
		return this._config;
	}

	public get mathVersion(): string {
		return '';
	}

	public get hasFreeSpinsRetrigger(): boolean {
		return this._hasFreeSpinsRetrigger;
	}

	public get freeSpinsAdded(): number {
		return this._freeSpinsAdded;
	}
}

export interface IPaytable {
	[index: number]: ISymbolValue[];
}

export interface ISymbolValue {
	id: number;
	values: {[index: number]: {count: number; winAmount: number}};
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/logic/Logic.ts